import { LineItem } from "@/models/order/order";
import { ProductType } from "@/models/products/product";

/**
 * FUPOF-193: which umbrella setup a cart line belongs to.
 *
 * A setup is one umbrella line plus the base and top-cover lines configured
 * with it on the umbrella page. `setup_id` is the membership key that ties them
 * together, and it is deliberately not `sd_share_id`: that one is a provenance
 * claim ("this line *is* design K7Q2M") that retires line by line the moment a
 * rep changes anything, is only present on short-code lines, and is shared by
 * every setup added from the same code. Membership has to survive edits, exist
 * for native adds too, and be unique per add. See
 * .claude/context/FUPOF-193/cart-grouping-spec.md §2.
 *
 * Stored in the same two places as `sd_share_id`, for the same reasons (see
 * the header of shared_design.ts):
 *
 *  - `item_details.setup_id` is the persisted copy. The Laravel API stores
 *    `item_details` verbatim but whitelists top-level line keys and drops
 *    anything else, so this is the copy that survives a draft round-trip.
 *  - `LineItem.setup_id` is the in-memory mirror, so a store-resident line —
 *    or one whose `item_details` the umbrella page has rebuilt — keeps it.
 *
 * Role within a setup comes from `product_type` (umbrella = anchor, base =
 * base, accessory = cover). There is no role field, because a stored role could
 * disagree with the type.
 */

/**
 * A fresh setup id: `"su_"` plus ten base-36 characters.
 *
 * Only has to be unique within one quote. `crypto.getRandomValues` rather than
 * a uuid library (none is installed, and none should be added for this) or
 * `crypto.randomUUID`, which this host supports but TS 3.9's lib.dom doesn't
 * type. The `% 36` bias (256 is not a multiple of 36) is irrelevant at this
 * scale: 36^10 ids, a handful of setups per quote.
 */
export const mintSetupId = (): string => {
  const bytes = new Uint8Array(10);
  window.crypto.getRandomValues(bytes);
  return `su_${Array.from(bytes, byte => (byte % 36).toString(36)).join("")}`;
};

/** The setup a line belongs to, read from either place it lives. */
export const setupIdOf = (item: LineItem): string | null => {
  if (!item) return null;
  if (item.setup_id) return item.setup_id;
  const details = item.item_details as { setup_id?: string } | undefined;
  return details?.setup_id ?? null;
};

/** Stamp a line (both locations) as a member of a setup. */
export const stampSetup = <T extends LineItem>(item: T, setupId: string): T => {
  item.setup_id = setupId;
  if (item.item_details) {
    (item.item_details as { setup_id?: string }).setup_id = setupId;
  }
  return item;
};

/** Take a line out of its setup, in both places the id lives. */
export const clearSetup = <T extends LineItem>(item: T): T => {
  delete item.setup_id;
  if (item.item_details) {
    delete (item.item_details as { setup_id?: string }).setup_id;
  }
  return item;
};

/** Where a setup's lines sit in the cart. Indices into the array searched. */
export interface SetupGroup {
  setupId: string;
  /** The umbrella line. */
  anchor: number;
  base: number | null;
  cover: number | null;
}

/**
 * The setups actually present in a cart, by the one render rule every consumer
 * shares (spec §2.5): an umbrella line carrying setup id S, followed by the
 * maximal **contiguous** run of base/accessory lines that also carry S, taking
 * at most one base and one cover.
 *
 * Anything else carrying S — a member separated from its umbrella, a second
 * base, members whose umbrella has been deleted — is not a member and is
 * treated as an ordinary standalone line. That is what lets bad data or an old
 * dev draft degrade to "ungrouped" rather than to "the wrong base gets edited".
 *
 * The cart pencil, OrderMixin.editItem and the umbrella page's setup editor all
 * go through this, so the three can never disagree about what a setup holds.
 */
export const findSetupGroups = (items: LineItem[]): SetupGroup[] => {
  const groups: SetupGroup[] = [];
  let i = 0;
  while (i < items.length) {
    const setupId = setupIdOf(items[i]);
    if (!setupId || items[i].product_type !== ProductType.Umbrella) {
      i++;
      continue;
    }
    const group: SetupGroup = { setupId, anchor: i, base: null, cover: null };
    let next = i + 1;
    for (; next < items.length; next++) {
      const line = items[next];
      if (setupIdOf(line) !== setupId) break;
      if (line.product_type === ProductType.Base && group.base === null) {
        group.base = next;
      } else if (
        line.product_type === ProductType.Accessory &&
        group.cover === null
      ) {
        group.cover = next;
      } else {
        // A second base or cover, or another umbrella: the run ends here.
        break;
      }
    }
    groups.push(group);
    i = next;
  }
  return groups;
};

/** The setup a given line is part of (as anchor or member), or null. */
export const setupGroupAt = (
  items: LineItem[],
  index: number
): SetupGroup | null =>
  findSetupGroups(items).find(
    group =>
      group.anchor === index || group.base === index || group.cover === index
  ) ?? null;

/**
 * FUPOF-193: the one quantity rule for a setup — every member (its base and
 * top cover) takes the umbrella's qty. A rep quoting three setups is quoting
 * three bases; the umbrella line is where the design, and now its quantity,
 * is controlled. The cart's umbrella stepper and the umbrella page's Update
 * both go through this, so the two can't disagree.
 *
 * All members follow, including one the rep had set to its own qty from its
 * own stepper. Only an umbrella that anchors a setup fans out: for any other
 * line — a member, a standalone base, a chair — the array comes back as it
 * was. Quantity *rules* (model minimums, even-qty stripes, acrylic fringe)
 * stay the umbrella's own; members simply take the qty the umbrella lands on.
 *
 * Changed lines are copied, never mutated, so this is safe on store state.
 * Returns the same array when there is nothing to change.
 */
export const syncSetupQty = (
  items: LineItem[],
  anchorIndex: number
): LineItem[] => {
  const group = setupGroupAt(items, anchorIndex);
  if (!group || group.anchor !== anchorIndex) return items;
  const qty = items[anchorIndex].qty;
  const members = [group.base, group.cover];
  if (!members.some(i => i !== null && items[i].qty !== qty)) return items;
  return items.map((line, i) =>
    members.indexOf(i) !== -1 && line.qty !== qty ? { ...line, qty } : line
  );
};
